// Overview — the employee's financial position at a glance. Composes payroll
// (payslips/YTD/trend), credit (facilities/schedules) and employee-service
// (payment methods) reads; every section fails independently (partial data),
// so a credit or profile outage never hides payroll.
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Receipt } from 'lucide-react';
import { useMyPaymentMethods } from '@/features/payment-methods/api/hooks';
import { track } from '@/lib/telemetry';
import type { PayslipSummary } from '../types';
import {
  useMyAnalytics,
  useMyFacilities,
  useMyOffers,
  useMyPayslip,
  useMyYtd,
  useOpenFacilitySchedules,
} from '../api/hooks';
import { useMyPayNav } from '../lib/nav';
import { groupUpcoming, type UpcomingSource } from '../lib/upcoming';
import { OverviewCards, type LoansCardData } from '../components/OverviewCards';
import { GrossToNetSnapshot } from '../components/GrossToNetSnapshot';
import { WhatChangedCard } from '../components/WhatChangedCard';
import { EmployerContributions } from '../components/EmployerContributions';
import { LoansSummary, OPEN_FACILITY_STATUSES, productName } from '../components/LoansSummary';
import { UpcomingDeductions } from '../components/UpcomingDeductions';
import { PaymentDestinationCard } from '../components/PaymentDestinationCard';
import { AnalyticsChart } from '../components/AnalyticsChart';
import { Skeleton } from '@/features/shared/components/ui/skeleton';

interface OverviewTabProps {
  payslips: PayslipSummary[];
  loading: boolean;
  revealed: boolean;
  onToggleReveal: () => void;
  creditEnabled: boolean;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ payslips, loading, revealed, onToggleReveal, creditEnabled }) => {
  const { t } = useTranslation('payroll');
  const { appBase, goTab } = useMyPayNav();
  const navigate = useNavigate();

  const latest = payslips[0];
  const prev = payslips[1];
  const latestDetailQ = useMyPayslip(latest?.id ?? null);
  const prevDetailQ = useMyPayslip(prev?.id ?? null);

  const ytdYear = useMemo(
    () => (latest?.payDate ? new Date(latest.payDate).getFullYear() : null),
    [latest],
  );
  const ytdQ = useMyYtd(ytdYear);

  const [months, setMonths] = useState(12);
  const analyticsQ = useMyAnalytics(months);

  const facilitiesQ = useMyFacilities(creditEnabled);
  const offersQ = useMyOffers(creditEnabled);
  const schedules = useOpenFacilitySchedules(facilitiesQ.data);
  const pmQ = useMyPaymentMethods();

  // Upcoming payroll impact from the repayment schedules (skip failed fetches).
  const upcoming = useMemo(() => {
    const sources: UpcomingSource[] = schedules.facilities.flatMap((f, i) => {
      const sched = schedules.schedules[i];
      if (!sched) return [];
      return [{
        facilityId: f.id,
        label: productName(f, offersQ.data) ?? t('myPay.genericLoan'),
        currency: f.currency,
        schedule: sched,
      }];
    });
    return groupUpcoming(sources);
  }, [schedules.facilities, schedules.schedules, offersQ.data, t]);

  // Loans card aggregate — never mix currencies into one number.
  const loansCard: LoansCardData = useMemo(() => {
    const open = (facilitiesQ.data ?? []).filter((f) => OPEN_FACILITY_STATUSES.includes(f.status));
    const currencies = new Set(open.map((f) => f.currency));
    const single = currencies.size === 1;
    return {
      enabled: creditEnabled,
      loading: creditEnabled && facilitiesQ.isLoading,
      error: facilitiesQ.isError,
      count: open.length,
      outstanding: single ? open.reduce((s, f) => s + f.outstandingBalance, 0) : 0,
      nextDeduction: single ? open.reduce((s, f) => s + f.installment, 0) : 0,
      currency: single && open.length > 0 ? open[0].currency : null,
      canRequest: (offersQ.data?.length ?? 0) > 0,
    };
  }, [creditEnabled, facilitiesQ.data, facilitiesQ.isLoading, facilitiesQ.isError, offersQ.data]);

  const openLoansModule = () => navigate(`${appBase}/loans`);

  return (
    <div className="space-y-6">
      <OverviewCards
        latest={latest}
        prev={prev}
        loading={loading}
        ytd={ytdQ.data}
        ytdLoading={ytdQ.isLoading}
        ytdError={ytdQ.isError}
        loans={loansCard}
        revealed={revealed}
        onToggleReveal={onToggleReveal}
        onOpenLoans={() => { track('loan_detail_opened'); goTab('loans'); }}
        onRequestLoan={openLoansModule}
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,26rem)]">
        <div className="space-y-6">
          {/* The latest pay, explained gross → net. */}
          <section aria-labelledby="mypay-snapshot">
            <h2 id="mypay-snapshot" className="mb-1 flex items-center gap-2 text-h3 text-charcoal">
              <Receipt className="h-5 w-5 text-[#D9711E]" /> {t('myPay.snapshotTitle')}
            </h2>
            {latest && (
              <p className="mb-3 text-xs text-muted-foreground">
                {latest.periodLabel} · <button type="button" className="underline hover:text-charcoal" onClick={() => goTab('payslips')}>{t('myPay.openFullStatement')}</button>
              </p>
            )}
            {latestDetailQ.isLoading ? (
              <Skeleton className="h-64 w-full rounded-xl" />
            ) : latestDetailQ.isError ? (
              <p className="rounded-xl border border-border px-4 py-6 text-center text-sm text-muted-foreground">{t('myPay.statementError')}</p>
            ) : latestDetailQ.data ? (
              <GrossToNetSnapshot detail={latestDetailQ.data} revealed={revealed} />
            ) : null}
          </section>

          <EmployerContributions
            detail={latestDetailQ.data}
            ytd={ytdQ.data}
            loading={latestDetailQ.isLoading}
            revealed={revealed}
          />
        </div>

        <div className="space-y-6">
          <WhatChangedCard
            latest={latestDetailQ.data}
            previous={prevDetailQ.data}
            loading={!!prev && (latestDetailQ.isLoading || prevDetailQ.isLoading)}
            revealed={revealed}
            onCompare={() => { track('periods_compared'); goTab('payslips', '?view=compare'); }}
          />
          <PaymentDestinationCard
            methods={pmQ.data}
            loading={pmQ.isLoading}
            error={pmQ.isError}
            onManage={() => navigate(`${appBase}/personal`)}
          />
          {creditEnabled && (
            <UpcomingDeductions periods={upcoming} loading={schedules.loading} revealed={revealed} />
          )}
        </div>
      </div>

      {creditEnabled && (
        <LoansSummary
          facilities={facilitiesQ.data}
          offers={offersQ.data}
          loading={facilitiesQ.isLoading}
          error={facilitiesQ.isError}
          revealed={revealed}
          onOpenLoans={() => { track('loan_detail_opened'); goTab('loans'); }}
          onOpenModule={openLoansModule}
        />
      )}

      <AnalyticsChart
        series={analyticsQ.data}
        loading={analyticsQ.isLoading}
        error={analyticsQ.isError}
        revealed={revealed}
        months={months}
        onMonths={setMonths}
        onRetry={() => analyticsQ.refetch()}
      />
    </div>
  );
};

export default OverviewTab;
